import { InboxOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Col, Form, Input, Row, Select, Space, Statistic, Table, Tag, Upload } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationGradeSelect,
  EducationSubjectSelect,
} from '@/pages/education/components/EducationLookupSelects';
import api from '@/services/api.service';

type ImportOutcome = {
  code: string;
  statement: string;
  sequence_no?: number;
  cognitive_level?: string;
};
type ImportUnit = {
  code: string;
  name: string;
  sequence_no?: number;
  description?: string;
  hours_hint?: number;
  outcomes: ImportOutcome[];
};
type Preview = {
  summary: Record<string, number>;
  units: Array<{
    code: string;
    name: string;
    action: 'create' | 'update';
    outcomes: Array<{ code: string; action: 'create' | 'update' }>;
  }>;
};
type ApplyResult = {
  dry_run: boolean;
  units_total: number;
  units_created: number;
  units_updated: number;
  outcomes_total: number;
  outcomes_created: number;
  outcomes_updated: number;
};
type PolicyPack = { id: string; pack_code: string; version?: string; status?: string };

const EXAMPLE = JSON.stringify(
  [
    {
      code: 'MATH-U01',
      name: 'Whole numbers',
      sequence_no: 1,
      description: 'Number sense and operations',
      hours_hint: 12,
      outcomes: [
        {
          code: 'MATH-U01-LO01',
          statement: 'Reads and compares whole numbers',
          sequence_no: 1,
          cognitive_level: 'understand',
        },
      ],
    },
  ],
  null,
  2
);

function parseUnits(value: string): ImportUnit[] {
  const parsed: unknown = JSON.parse(value);
  const units =
    Array.isArray(parsed)
      ? parsed
      : parsed && typeof parsed === 'object' && Array.isArray((parsed as { units?: unknown }).units)
        ? (parsed as { units: unknown[] }).units
        : null;
  if (!units) throw new Error('JSON must be an array of units or an object with a units array');
  return units as ImportUnit[];
}

function errorMessage(error: unknown, fallback: string): string {
  const candidate = error as { response?: { data?: { message?: string } }; message?: string };
  return candidate.response?.data?.message || candidate.message || fallback;
}

export default function CurriculumImportPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [subjectId, setSubjectId] = useState<string>();
  const [gradeLevelId, setGradeLevelId] = useState<string>();
  const [policyPackId, setPolicyPackId] = useState<string>();
  const [jsonText, setJsonText] = useState(EXAMPLE);
  const [preview, setPreview] = useState<Preview>();
  const [applied, setApplied] = useState<ApplyResult>();

  const packs = useQuery({
    queryKey: ['education-policy-packs'],
    queryFn: async () => (await api.get<PolicyPack[]>('/education/policy-packs')).data ?? [],
  });

  const payload = useMemo(() => {
    if (!subjectId || !gradeLevelId) return null;
    try {
      return {
        subject_id: subjectId,
        grade_level_id: gradeLevelId,
        policy_pack_id: policyPackId || null,
        units: parseUnits(jsonText),
      };
    } catch {
      return null;
    }
  }, [gradeLevelId, jsonText, policyPackId, subjectId]);

  const previewMutation = useMutation({
    mutationFn: async () => {
      if (!subjectId || !gradeLevelId) throw new Error(t('curriculumImportSubjectGradeRequired'));
      const units = parseUnits(jsonText);
      return (
        await api.post<Preview>('/education/curriculum/import/preview', {
          subject_id: subjectId,
          grade_level_id: gradeLevelId,
          policy_pack_id: policyPackId || null,
          units,
        })
      ).data;
    },
    onSuccess: (data) => {
      setPreview(data);
      setApplied(undefined);
    },
    onError: (error) => message.error(errorMessage(error, t('curriculumImportInvalid'))),
  });

  const applyMutation = useMutation({
    mutationFn: async () => {
      if (!payload) throw new Error(t('curriculumImportInvalid'));
      return (await api.post<ApplyResult>('/education/curriculum/import/apply', payload)).data;
    },
    onSuccess: async (data) => {
      setApplied(data);
      setPreview(undefined);
      await queryClient.invalidateQueries({ queryKey: ['education-curriculum-units'] });
      message.success(t('curriculumImportApplied'));
    },
    onError: (error) => message.error(errorMessage(error, t('curriculumImportFailed'))),
  });

  const resetPreview = () => {
    setPreview(undefined);
    setApplied(undefined);
  };

  return (
    <EducationPageChrome
      title={t('curriculumImportTitle')}
      description={t('curriculumImportIntro')}
      extra={<Button onClick={() => navigate('/education/curriculum')}>{t('backToCurriculum')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('curriculumImportHumanAuthority')} />
        <Card size="small" title={t('curriculumImportScope')}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item label={t('subject')} required>
                <EducationSubjectSelect
                  value={subjectId}
                  onChange={(value) => {
                    setSubjectId(value as string | undefined);
                    resetPreview();
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={t('grade')} required>
                <EducationGradeSelect
                  value={gradeLevelId}
                  onChange={(value) => {
                    setGradeLevelId(value as string | undefined);
                    resetPreview();
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={t('curriculumPolicyPackOptional')}>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  loading={packs.isLoading}
                  value={policyPackId}
                  onChange={(value) => {
                    setPolicyPackId(value);
                    resetPreview();
                  }}
                  options={(packs.data ?? []).map((pack) => ({
                    value: pack.id,
                    label: `${pack.pack_code}${pack.version ? ` — ${pack.version}` : ''}`,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card size="small" title={t('curriculumImportCatalogue')}>
          <Upload.Dragger
            accept=".json,application/json"
            maxCount={1}
            showUploadList={false}
            beforeUpload={async (file) => {
              try {
                const text = await file.text();
                parseUnits(text);
                setJsonText(text);
                resetPreview();
                message.success(t('curriculumImportFileLoaded', { name: file.name }));
              } catch (error) {
                message.error(errorMessage(error, t('curriculumImportInvalid')));
              }
              return false;
            }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">{t('curriculumImportUpload')}</p>
            <p className="ant-upload-hint">{t('curriculumImportUploadHint')}</p>
          </Upload.Dragger>
          <Input.TextArea
            value={jsonText}
            onChange={(event) => {
              setJsonText(event.target.value);
              resetPreview();
            }}
            rows={18}
            spellCheck={false}
            style={{ marginTop: 16, fontFamily: 'monospace' }}
          />
          <Space style={{ marginTop: 16 }}>
            <Button
              type="primary"
              loading={previewMutation.isPending}
              onClick={() => previewMutation.mutate()}
            >
              {t('curriculumImportDryRun')}
            </Button>
          </Space>
        </Card>

        {preview ? (
          <Card size="small" title={t('curriculumImportPreview')}>
            <Alert
              type="warning"
              showIcon
              message={t('curriculumImportNothingSaved')}
              style={{ marginBottom: 16 }}
            />
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
              {Object.entries(preview.summary).map(([key, value]) => (
                <Col xs={12} md={6} key={key}>
                  <Card size="small"><Statistic title={t(key, { defaultValue: key })} value={value} /></Card>
                </Col>
              ))}
            </Row>
            <Table
              rowKey="code"
              size="small"
              pagination={{ pageSize: 20 }}
              dataSource={preview.units}
              columns={[
                { title: t('code'), dataIndex: 'code', width: 160 },
                { title: t('name'), dataIndex: 'name' },
                {
                  title: t('curriculumImportAction'),
                  dataIndex: 'action',
                  width: 120,
                  render: (value: string) => <Tag color={value === 'create' ? 'green' : 'blue'}>{value}</Tag>,
                },
                {
                  title: t('outcomes'),
                  render: (_value, row) => row.outcomes.length,
                  width: 100,
                },
              ]}
            />
            <Button
              type="primary"
              danger
              loading={applyMutation.isPending}
              onClick={() => applyMutation.mutate()}
              style={{ marginTop: 16 }}
            >
              {t('curriculumImportCommit')}
            </Button>
          </Card>
        ) : null}

        {applied ? (
          <Alert
            type="success"
            showIcon
            message={t('curriculumImportApplied')}
            description={Object.entries(applied)
              .filter(([, value]) => typeof value === 'number')
              .map(([key, value]) => `${t(key, { defaultValue: key })}: ${value}`)
              .join(' · ')}
          />
        ) : null}
      </Space>
    </EducationPageChrome>
  );
}
